import {version} from '../../package.json';
import Environment from './Environment';
import * as Sentry from '@sentry/react';

const metaPayload = () => {
  return {
    version,
    environment: Environment.getVar('REACT_APP_ENV') ?? 'test',
    host: window.location.host,
    url: window.location.href,
    urlParams: window.location.search,
  };
};

/**
 * Milliseconds since the page loaded, stamped onto every tracked event so the whole
 * funnel is time-annotated (how long until CTA click, form focus, payment-sheet open,
 * dismiss, etc.). Uses `performance.now()` — monotonic and independent of wall-clock
 * changes. Note: this is time since page *load*, so for screens reached via SPA
 * navigation it spans the earlier screens too, not just the current one.
 */
const dwellPayload = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? {dwellMs: Math.round(performance.now())}
    : {};

/**
 * Ad and content blockers can stop the Segment snippet in public/index.html from defining `window.analytics` at all,
 * and calling into it then throws ("undefined is not an object (evaluating 'ny.track')") from whichever screen tracked
 * first. Every call goes through here so a blocked Segment drops the event instead of breaking the page.
 */
const segment = (): any => (typeof window !== 'undefined' && window.analytics ? window.analytics : undefined);

function track(eventName: string, payload: object = {}) {
  const completePayload = {...metaPayload(), ...dwellPayload(), ...payload};
  if (typeof segment()?.track === 'function') {
    window.analytics.track(eventName, completePayload);
  }

  if (Environment.inTest) {
    logAnalytics(eventName, completePayload);
  }
}

// Public Segment write key — mirrors the analytics.js snippet in public/index.html.
// Safe to embed here; it already ships to every client in that snippet.
const SEGMENT_WRITE_KEY = 'PQDuqRYEiDqEELqHYjf8upnQ7c6PFGNu';

/**
 * Reliably deliver an event while the page is being hidden/unloaded.
 *
 * Classic analytics.js `track()` (this repo's Segment loader) sends an async XHR that the
 * browser can cancel when the page is frozen or discarded — common on mobile app-switch /
 * tab-close — so events fired from `pagehide` / `visibilitychange -> hidden` handlers are
 * silently lost. `navigator.sendBeacon` is the one transport the browser guarantees to
 * flush as the page goes away, so we post a Segment-formatted event to the HTTP Tracking
 * API directly. Identity (`anonymousId`/`userId`) is read from the live analytics instance
 * so the event still stitches to the user. Content-Type `text/plain` avoids a CORS
 * preflight the unloading page wouldn't survive. Falls back to a best-effort `track()`.
 */
function trackOnLeave(eventName: string, payload: object = {}) {
  const properties = {...metaPayload(), ...dwellPayload(), ...payload};
  const canBeacon = typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
  if (canBeacon && analyticsIsReady()) {
    try {
      const user = window.analytics.user && window.analytics.user();
      const body = JSON.stringify({
        writeKey: SEGMENT_WRITE_KEY,
        event: eventName,
        properties,
        anonymousId: user && user.anonymousId ? user.anonymousId() : undefined,
        userId: user && user.id ? user.id() : undefined,
        timestamp: new Date().toISOString(),
        context: {library: {name: 'sendBeacon', version}},
      });
      const queued = navigator.sendBeacon('https://api.segment.io/v1/track', new Blob([body], {type: 'text/plain'}));
      if (queued) {
        if (Environment.inTest) {
          logAnalytics(eventName, properties);
        }
        return;
      }
    } catch {
      // fall through to the best-effort track below
    }
  }
  track(eventName, payload);
}

const logAnalytics = (eventName: string, completePayload?: object) => {
  console.log(`Sent to Amplitude: "${eventName}". Payload: ${JSON.stringify(completePayload, null, '\t')}`);
};

const PERSON_ID = /^\d+$/;

type IdentifyPayload = Record<string, any>;
type AnonymousIdentify = (payload: IdentifyPayload) => void;
type IdentifiedIdentify = (id: string | number, payload?: IdentifyPayload) => void;
const identify: IdentifiedIdentify & AnonymousIdentify = (
  idOrPayload: string | number | IdentifyPayload,
  payload?: IdentifyPayload,
) => {
  if (typeof idOrPayload === 'string' || typeof idOrPayload === 'number') {
    //id if known id
    if (typeof segment()?.identify === 'function') {
      window.analytics.identify(idOrPayload, {...metaPayload(), ...payload});
    }
    // Lets a Sentry event be traced to a person. The person id only: some callers identify by
    // email, and nothing else from the payload belongs in Sentry.
    if (PERSON_ID.test(String(idOrPayload))) {
      Sentry.setUser({id: String(idOrPayload)});
    }
  } else {
    //payload if anonymous id
    if (typeof segment()?.identify === 'function') {
      window.analytics.identify({...metaPayload(), ...idOrPayload});
    }
  }
};

const waitFor = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const analyticsIsReady = () => !Array.isArray(window.analytics);

/**
 * In some cases, we need to ensure that Segment is initialized before sending events
 * (redirecting back to the Checkout Success Page from Stripe Checkout is one example)
 * By default: waits 200 ms and retries 5 times.
 */
const onReady = async (analytics: () => void, retries: number = 5, waitInMs: number = 200): Promise<void> => {
  for (let i = 0; i < retries; i++) {
    if (analyticsIsReady()) {
      analytics();
      return;
    }
    await waitFor(waitInMs);
  }
  Sentry.captureException(new Error("Analytics didn't initialize within window"));
};

const Analytics = {
  track,
  trackOnLeave,
  identify,
  onReady,
};
export default Analytics;
